import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import Input from '../ui/Input';
import RequiredDocumentsPanel from './RequiredDocumentsPanel';
import RejectDialog from './RejectDialog';
import FieldAnswerList from '../fields/FieldAnswerList';
import { IconArrowRight, IconCheckCircle, IconEdit, IconRefresh, IconSend, IconSkipForward, IconXCircle } from '../ui/icons';
import AttemptHistoryModal from './AttemptHistoryModal';
import { EditRequestModal, PendingEditBanner } from './StepEditRequest';
import { processCandidateStep, requestStepApproval } from '../../api/candidates';
import { pushToast } from '../ui/toastBus';
import { usePermission } from '../../auth/usePermission';
import { formatValidationErrors } from '../../utils/errors';
import { groupFieldsBySection } from '../../utils/fieldSections';

const META_FIELD = {
  swipe_card: { key: 'employee_no', label: 'Employee No', required: true },
  permit_issuance: { key: 'permit_no', label: 'Permit No', required: false },
};

const FIELDS_GRID = 'grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2';

function actionsFor(step) {
  if (step.kind !== 'decision') return [null, null];
  const positive = step.decision_labels?.positive === 'Approved' ? 'approve' : 'pass';
  const negative = step.decision_labels?.negative === 'Rejected' ? 'reject' : 'fail';
  return [positive, negative];
}

function ActionBar({ slot, children }) {
  return slot ? createPortal(children, slot) : <div className="flex flex-wrap gap-3">{children}</div>;
}

export default function CurrentStepPanel({ candidate, step }) {
  // Action buttons render into the workspace modal's fixed footer when it's present.
  const [actionSlot, setActionSlot] = useState(null);
  useEffect(() => setActionSlot(document.getElementById('step-action-bar')), []);
  const queryClient = useQueryClient();
  const { can } = usePermission();

  // allowed_actions from the candidate is only computed for the literal current step; a step
  // being caught up out of order needs the same permission + document check done locally.
  const permissionName = `step.process.${step.code}`;
  const assignedElsewhere = step.assignment?.locked ? step.assignment.assigned_to : null;
  const hasPermission = can(permissionName) && !assignedElsewhere;
  const allowed = step.is_current
    ? candidate.allowed_actions
    : {
        can_process: hasPermission && (step.missing_documents?.length ?? 0) === 0,
        missing_permission: hasPermission ? null : permissionName,
        missing_documents: step.missing_documents || [],
      };
  // Skip bypasses documents/fields entirely, so it's gated on permission alone — not on the
  // same missing-documents check that blocks the normal complete/approve/reject actions.
  // Already-skipped steps don't offer it again — they're here to be completed, not re-skipped.
  const canSkip = step.skip_enabled && hasPermission && step.status !== 'skipped';
  // The server already confirms this is the current in-progress task step with at least one
  // section saved — documents/checklist/other sections don't gate moving on early.
  const canMoveOn = !!step.can_move_on && hasPermission;

  const [remarks, setRemarks] = useState(step.remarks || '');
  const [checklistState, setChecklistState] = useState(step.checklist_state || {});
  const [answers, setAnswers] = useState(step.answers || {});
  const [metaValue, setMetaValue] = useState(step.meta?.[META_FIELD[step.code]?.key] || '');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const hasCounter = step.kind === 'decision' && !!step.attempt_max && !step.approval_level;
  const historyCount = step.attempt_history?.length || 0;

  // A failed attempt clears (some of) the form server-side — re-sync the local copy from the refetched step.
  useEffect(() => {
    setAnswers(step.answers || {});
    setRemarks(step.remarks || '');
    setChecklistState(step.checklist_state || {});
    // Baseline = what the server now holds, so the first edit after a reset still gets saved.
    lastSaved.current = JSON.stringify({
      action: 'save_draft',
      remarks: step.remarks || '',
      checklist_state: step.checklist_state || {},
      answers: step.answers || {},
      ...(metaField ? { meta: step.meta?.[metaField.key] ? { [metaField.key]: step.meta[metaField.key] } : {} } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.attempt_count, step.status]);
  const [editSection, setEditSection] = useState(null);
  const canRequestEdit = can('edit candidate completed step');

  const metaField = META_FIELD[step.code];

  const [positiveAction, negativeAction] = actionsFor(step);
  const [requestedOutcome, setRequestedOutcome] = useState(positiveAction);
  const pendingApproval = step.approval_request?.status === 'pending' ? step.approval_request : null;
  const rejectedApproval = step.approval_request?.status === 'rejected' ? step.approval_request : null;

  const mutation = useMutation({
    mutationFn: (payload) => processCandidateStep(candidate.id, step.candidate_step_id, payload),
    onSuccess: (data) => {
      pushToast({
        type: 'success',
        message: data?.submitted_for_approval ? 'Submitted for approval — it takes effect once approved.' : 'Step updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
      setRejectOpen(false);
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not process step.';
      pushToast({ type: 'error', message });
    },
  });

  // Separate from `mutation` so a section Update / Next click doesn't show the main Complete
  // button as loading (and vice versa).
  const sectionMutation = useMutation({
    mutationFn: (payload) => processCandidateStep(candidate.id, step.candidate_step_id, payload),
    onSuccess: (_data, variables) => {
      pushToast({ type: 'success', message: variables.action === 'next' ? 'Moved to next step.' : 'Section saved.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not save section.';
      pushToast({ type: 'error', message });
    },
  });

  const approvalMutation = useMutation({
    mutationFn: (payload) => requestStepApproval(candidate.id, step.candidate_step_id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Approval requested.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not request approval.';
      pushToast({ type: 'error', message });
    },
  });

  const buildPayload = (action, overrideRemarks) => ({
    action,
    remarks: overrideRemarks ?? remarks,
    checklist_state: checklistState,
    answers,
    meta: metaField && metaValue ? { [metaField.key]: metaValue } : undefined,
  });

  const handlePositive = (action) => mutation.mutate(buildPayload(action));
  const handleReject = (action, reason) => mutation.mutate(buildPayload(action, reason));
  const buildDraft = () => ({
    action: 'save_draft',
    remarks,
    checklist_state: checklistState,
    answers,
    ...(metaField ? { meta: metaValue ? { [metaField.key]: metaValue } : {} } : {}),
  });

  // Autosave: whatever staff type is saved shortly after they stop, without finishing the step.
  const draftEnabled = hasPermission && !pendingApproval && ['pending', 'in_progress', 'failed', 'skipped'].includes(step.status);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const lastSaved = useRef(null);
  const latest = useRef({ draft: null, enabled: false });
  latest.current = { draft: buildDraft(), enabled: draftEnabled };
  const draftJson = JSON.stringify(latest.current.draft);

  const saveDraft = (draft) => {
    lastSaved.current = JSON.stringify(draft);
    setSaveState('saving');
    return processCandidateStep(candidate.id, step.candidate_step_id, draft)
      .then(() => {
        setSaveState('saved');
        // Write the saved values into the cached candidate so reopening this popup shows them
        // straight away (the page's query stays mounted, so a plain invalidation wouldn't refetch).
        queryClient.setQueryData(['candidates', String(candidate.id)], (old) =>
          old
            ? {
                ...old,
                steps: (old.steps || []).map((st) =>
                  st.candidate_step_id === step.candidate_step_id
                    ? {
                        ...st,
                        answers: draft.answers,
                        remarks: draft.remarks,
                        checklist_state: draft.checklist_state,
                        meta: draft.meta ? { ...(st.meta || {}), ...draft.meta } : st.meta,
                      }
                    : st
                ),
              }
            : old
        );
      })
      .catch(() => {
        lastSaved.current = null; // retry on the next change
        setSaveState('error');
      });
  };

  useEffect(() => {
    // First render (or after a server-side reset) just records the baseline — nothing to save yet.
    if (lastSaved.current === null) {
      lastSaved.current = draftJson;
      return undefined;
    }
    if (!draftEnabled || draftJson === lastSaved.current) return undefined;
    const timer = setTimeout(() => saveDraft(latest.current.draft), 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJson, draftEnabled]);

  // Closing the panel inside the debounce window must not lose the last keystrokes.
  useEffect(
    () => () => {
      const { draft, enabled } = latest.current;
      if (enabled && JSON.stringify(draft) !== lastSaved.current) saveDraft(draft);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleAttempt = () => mutation.mutate(buildPayload('attempt'));
  const handleSkip = () => mutation.mutate(buildPayload('skip'));
  const handleRequestApproval = () => {
    approvalMutation.mutate({
      proposed_action: step.kind === 'task' ? 'complete' : requestedOutcome,
      remarks,
      checklist_state: checklistState,
      answers,
      meta: metaField && metaValue ? { [metaField.key]: metaValue } : undefined,
    });
  };

  const saveSection = (sectionId, sectionFields) => {
    const sectionAnswers = {};
    sectionFields.forEach((f) => {
      sectionAnswers[f.id] = answers[f.id];
    });
    sectionMutation.mutate({ action: 'save_section', section_id: sectionId, answers: sectionAnswers });
  };
  const moveOn = () => sectionMutation.mutate({ action: 'next' });

  const toggleChecklistItem = (item) => {
    setChecklistState((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const setAnswer = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }));

  const isReprocessableFail = step.status === 'failed' && step.negative_outcome === 'company_policy';

  // Field sections only drive incremental save + early "Next" for task steps — a decision step
  // is a single all-or-nothing action, so its fields (sectioned or not) render as one flat form.
  const sections = step.kind === 'task' ? step.field_sections || [] : [];
  const bySection = sections.length > 0 ? groupFieldsBySection(step.fields || []) : null;
  const ungroupedFields = bySection ? bySection.get(null) || [] : step.fields || [];

  return (
    <div className="rounded-lg border border-primary-light bg-primary-50/40 p-4">
      {step.description && <p className="mb-3 text-sm text-gray-600">{step.description}</p>}

      {(hasCounter || historyCount > 0) && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md bg-white/70 px-3 py-2 text-sm">
          {hasCounter && (
            <span className="font-medium text-gray-800">
              Attempt {Math.min(step.attempt_count + 1, step.attempt_max)} of {step.attempt_max}
              <span className="ml-2 text-xs font-normal text-gray-500">
                ({step.attempt_count} failed so far)
              </span>
            </span>
          )}
          {historyCount > 0 && (
            <button type="button" onClick={() => setHistoryOpen(true)} className="text-xs text-primary hover:underline">
              View history ({historyCount})
            </button>
          )}
        </div>
      )}
      <AttemptHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} step={step} />

      {isReprocessableFail && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Previous attempt failed{step.meta?.attempts?.length ? ` (${step.meta.attempts.length} attempt(s))` : ''} —
          you can retry.
        </p>
      )}

      {step.checklist?.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Checklist</h4>
          <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
            {step.checklist.map((item) => (
              <label key={item} className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="accent-primary" checked={!!checklistState[item]} onChange={() => toggleChecklistItem(item)} />
                {item}
              </label>
            ))}
          </div>
        </div>
      )}

      <PendingEditBanner candidate={candidate} step={step} />

      {bySection &&
        sections.map((section) => {
          const sectionFields = bySection.get(section.id) || [];
          if (sectionFields.length === 0) return null;
          const saved = (step.completed_sections || []).includes(section.id);

          return (
            <div key={section.id} className="mb-4 rounded-md border border-gray-200 bg-white/60 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {section.title}
                  {saved && <span className="ml-2 normal-case text-green-600">✓ Saved</span>}
                </h4>
                {!saved && hasPermission && (
                  <Button
                    variant="secondary"
                    className="px-3 py-1.5 text-xs"
                    onClick={() => saveSection(section.id, sectionFields)}
                    loading={sectionMutation.isPending && sectionMutation.variables?.section_id === section.id}
                  >
                    <IconRefresh className="h-3.5 w-3.5" />
                    Update section
                  </Button>
                )}
                {saved && !step.pending_edit_request && canRequestEdit && (
                  <Button variant="secondary" className="px-3 py-1.5 text-xs" onClick={() => setEditSection(section)}>
                    <IconEdit className="h-3.5 w-3.5" />
                    Request edit
                  </Button>
                )}
              </div>
              {saved ? (
                <div className="pointer-events-none opacity-60">
                  <FieldAnswerList fields={sectionFields} answers={step.answers} onAnswer={() => {}} className={FIELDS_GRID} />
                </div>
              ) : (
                <FieldAnswerList fields={sectionFields} answers={answers} onAnswer={setAnswer} className={FIELDS_GRID} />
              )}
            </div>
          );
        })}

      {editSection && (
        <EditRequestModal
          open={!!editSection}
          onClose={() => setEditSection(null)}
          candidate={candidate}
          step={step}
          sectionId={editSection.id}
          sectionTitle={editSection.title}
          fields={bySection?.get(editSection.id) || []}
        />
      )}

      {ungroupedFields.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{sections.length > 0 ? 'Other fields' : 'Fields'}</h4>
          <FieldAnswerList fields={ungroupedFields} answers={answers} onAnswer={setAnswer} className={FIELDS_GRID} />
        </div>
      )}

      {step.required_documents?.length > 0 && (
        <div className="mb-4">
          <RequiredDocumentsPanel
            requiredDocuments={step.required_documents}
            documents={candidate.documents}
            candidateId={candidate.id}
            candidateStepId={step.candidate_step_id}
            fields={step.fields}
            answers={answers}
          />
        </div>
      )}

      {metaField && (
        <Input
          label={metaField.label}
          required={metaField.required}
          value={metaValue}
          onChange={(e) => setMetaValue(e.target.value)}
          className="mb-4"
        />
      )}

      <Textarea label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mb-1" />
      {draftEnabled && (
        <p className={`mb-4 text-xs ${saveState === 'error' ? 'text-red-600' : 'text-gray-400'}`}>
          {saveState === 'saving' && 'Saving…'}
          {saveState === 'saved' && 'Draft saved automatically.'}
          {saveState === 'error' && "Couldn't autosave — your changes are still here; they'll retry on the next edit."}
          {saveState === 'idle' && 'Changes are saved automatically.'}
        </p>
      )}

      {rejectedApproval && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-medium">Approval rejected by {rejectedApproval.reviewed_by}</p>
          {rejectedApproval.review_remarks && <p className="mt-1 text-amber-800">"{rejectedApproval.review_remarks}"</p>}
        </div>
      )}

      {step.approval_level ? (
        pendingApproval ? (
          <p className="rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-900">
            Awaiting level {pendingApproval.level} approval — requested by {pendingApproval.requested_by} on{' '}
            {new Date(pendingApproval.requested_at).toLocaleString()}.
          </p>
        ) : hasPermission ? (
          <div className="flex flex-wrap items-center gap-3">
            {step.kind === 'decision' && (
              <select
                className="rounded-md border border-gray-300 px-3 py-2 text-sm capitalize"
                value={requestedOutcome}
                onChange={(e) => setRequestedOutcome(e.target.value)}
              >
                <option value={positiveAction} className="capitalize">
                  {positiveAction}
                </option>
                <option value={negativeAction} className="capitalize">
                  {negativeAction}
                </option>
              </select>
            )}
            <Button onClick={handleRequestApproval} loading={approvalMutation.isPending}>
              <IconSend className="h-4 w-4" />
              Request Approval — Level {step.approval_level}
            </Button>
          </div>
        ) : (
          <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">Requires permission: {permissionName}</p>
        )
      ) : pendingApproval ? (
        <p className="rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-900">
          <span className="font-medium capitalize">{pendingApproval.proposed_action === 'attempt' ? 'Fail attempt' : pendingApproval.proposed_action}</span> request awaiting approval — submitted by{' '}
          {pendingApproval.requested_by} on {new Date(pendingApproval.requested_at).toLocaleString()}.
        </p>
      ) : allowed.can_process || canSkip || canMoveOn ? (
        <ActionBar slot={actionSlot}>
          {allowed.can_process &&
            (step.kind === 'task' ? (
              <Button onClick={() => handlePositive('complete')} loading={mutation.isPending}>
                <IconCheckCircle className="h-4 w-4" />
                Mark Complete
              </Button>
            ) : (
              <>
                <Button variant="success" onClick={() => handlePositive(positiveAction)} loading={mutation.isPending} className="capitalize">
                  <IconCheckCircle className="h-4 w-4" />
                  {positiveAction}
                </Button>
                {hasCounter ? (
                  <Button variant="danger" onClick={handleAttempt} loading={mutation.isPending}>
                    <IconXCircle className="h-4 w-4" />
                    Fail attempt {Math.min(step.attempt_count + 1, step.attempt_max)}/{step.attempt_max}
                  </Button>
                ) : (
                  <Button variant="danger" onClick={() => setRejectOpen(true)} disabled={mutation.isPending} className="capitalize">
                    <IconXCircle className="h-4 w-4" />
                    {negativeAction}
                  </Button>
                )}
              </>
            ))}
          {canSkip && (
            <Button variant="secondary" onClick={handleSkip} loading={mutation.isPending}>
              <IconSkipForward className="h-4 w-4" />
              Skip Step
            </Button>
          )}
          {canMoveOn && (
            <Button variant="secondary" onClick={moveOn} loading={sectionMutation.isPending && sectionMutation.variables?.action === 'next'}>
              Next step
              <IconArrowRight className="h-4 w-4" />
            </Button>
          )}
        </ActionBar>
      ) : (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
          {assignedElsewhere || allowed.assigned_to
            ? `Assigned to ${assignedElsewhere || allowed.assigned_to} — only they can update this step.`
            : allowed.missing_permission
            ? `Requires permission: ${allowed.missing_permission}`
            : allowed.missing_documents?.length > 0
              ? `Missing verified documents: ${allowed.missing_documents.join(', ')}`
              : 'You cannot process this step.'}
        </p>
      )}

      {step.kind === 'decision' && (
        <RejectDialog
          open={rejectOpen}
          onClose={() => setRejectOpen(false)}
          onConfirm={(reason) => handleReject(negativeAction, reason)}
          loading={mutation.isPending}
          actionLabel={negativeAction === 'reject' ? 'Reject' : 'Fail'}
          closesRecruitment={step.negative_outcome === 'reject_candidate'}
        />
      )}
    </div>
  );
}
